import React from 'react';

import { OwnWindowContext } from '../../../dialog/own-window';

/** The space kept below the dialog, as the dialogs keep from the window's edges. */
const viewportEdge = 5;
/** The lowest it gets, so that a dialog far down still shows some of its content. */
const minHeight = 120;

/**
 * What follows the content in its dialog: the padding and borders around it and what is after it,
 * e.g. a footer. Not the dialog's bottom, which is the window's where the dialog fills a window of
 * its own, below the content.
 */
const getBelow = (scroll: HTMLElement) => {
  const dialog = scroll.closest('[role="dialog"]');
  let below = 0;
  for (let element: Element = scroll; element !== dialog && element.parentElement; ) {
    const parent: HTMLElement = element.parentElement;
    for (let next = element.nextElementSibling; next; next = next.nextElementSibling) {
      below += next.getBoundingClientRect().height;
    }
    const style = (parent.ownerDocument.defaultView ?? window).getComputedStyle(parent);
    below += (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
    element = parent;
  }
  return below;
};

/**
 * Keeps the content of a dialog within the window below its top, scrolling what doesn't fit, with
 * what follows it in the dialog always in view below it, e.g. the snapping dialog's tooltip in a
 * band. It fits again as what follows grows or shrinks, e.g. the tooltip while measuring, which
 * makes the content shorter rather than scrolling it, so that nothing moves under the cursor.
 *
 * In the window the dialog is in, its own once popped out, which it fills to the bottom: used
 * within the dialog, which tells it.
 */
export const useFitsInWindow = (
  scrollRef: React.RefObject<HTMLElement>,
  following: HTMLElement | null,
) => {
  const ownWindow = React.useContext(OwnWindowContext);

  React.useEffect(() => {
    const scroll = scrollRef.current;
    const view = ownWindow ?? window;
    const margin = ownWindow ? 0 : viewportEdge;
    if (!scroll || typeof ResizeObserver === 'undefined') {
      return;
    }

    const fit = () => {
      const { top } = scroll.getBoundingClientRect();
      scroll.style.maxHeight = `${Math.max(minHeight, view.innerHeight - top - getBelow(scroll) - margin)}px`;
    };

    fit();
    // Also where the dialog was moved since, it fits there
    const observer = new ResizeObserver(fit);
    observer.observe(scroll);
    if (following) {
      observer.observe(following);
    }
    view.addEventListener('resize', fit);
    // Moved without resizing, it fits where it is dropped. Not as it is dragged, which would
    // measure it at every movement of the cursor, also in the view while measuring.
    view.addEventListener('pointerup', fit);
    return () => {
      observer.disconnect();
      view.removeEventListener('resize', fit);
      view.removeEventListener('pointerup', fit);
    };
  }, [scrollRef, following, ownWindow]);
};
